import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle, Clock, ThumbsUp, Prohibit, SpinnerGap, UserCircle, Trophy } from "@phosphor-icons/react";
import GuideLink from "@/components/GuideLink";

const STATUS = {
  pending: { label: "Pending", cls: "text-amber-700 bg-amber-50 border-amber-200", Icon: Clock },
  approved: { label: "Approved · Candidate", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: Trophy },
  rejected: { label: "Rejected", cls: "text-red-700 bg-red-50 border-red-200", Icon: XCircle },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.pending;
  const Icon = s.Icon;
  return (
    <span data-testid={`nom-status-${status}`} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border ${s.cls}`}>
      <Icon size={13} weight="fill" /> {s.label}
    </span>
  );
}

export default function NominationsTab({ electionId, election, onChange }) {
  const [nominees, setNominees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: noms }, tgt] = await Promise.all([
        api.get(`/elections/${electionId}/voters`),
        election.nomination_for ? api.get(`/elections/${election.nomination_for}`).catch(() => ({ data: { positions: [] } })) : Promise.resolve({ data: { positions: [] } }),
      ]);
      setNominees(noms || []);
      setPositions((tgt.data.positions || []).map((p) => p.title));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [electionId, election.nomination_for]);

  useEffect(() => { load(); }, [load]);

  const approve = async (n) => {
    setBusy(n.id);
    try {
      const { data } = await api.post(`/elections/${electionId}/voters/${n.id}/approve`);
      if (data.promoted) toast.success(`${n.name} approved — now a candidate`);
      else toast.success(`${n.name} approved${data.note ? ` (${data.note})` : ""}`);
      await load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const approveAll = async (position, items) => {
    const pending = items.filter((n) => n.status === "pending");
    if (!pending.length) return;
    setBusy(`group-${position}`);
    try {
      const { data } = await api.post(`/elections/${electionId}/voters/approve-bulk`, { voter_ids: pending.map((n) => n.id) });
      toast.success(`Approved ${data.approved} nominee${data.approved === 1 ? "" : "s"} for ${position}${data.invited ? ` · ${data.invited} now candidate${data.invited === 1 ? "" : "s"}` : ""}`);
      await load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const doReject = async () => {
    if (!rejectTarget) return;
    setBusy(rejectTarget.id);
    try {
      await api.post(`/elections/${electionId}/voters/${rejectTarget.id}/reject`, { reason: rejectReason });
      toast.success("Nomination rejected");
      setRejectTarget(null); setRejectReason("");
      await load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const posSought = (n) => ((n.custom_fields || {}).position_sought || "").trim();
  const allPositions = [...new Set([...positions, ...nominees.map(posSought).filter(Boolean)])];
  const grouped = allPositions.map((p) => ({ position: p, items: nominees.filter((n) => posSought(n) === p) }));
  const noPosition = nominees.filter((n) => !posSought(n));
  if (noPosition.length) grouped.push({ position: "Unspecified position", items: noPosition });

  const pendingCount = nominees.filter((n) => n.status === "pending").length;
  const approvedCount = nominees.filter((n) => n.status === "approved").length;

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-zinc-400"><SpinnerGap size={24} className="animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="nominations-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-zinc-600" data-testid="nom-summary">
          <span className="font-semibold text-black">{nominees.length}</span> nominee{nominees.length === 1 ? "" : "s"} across{" "}
          <span className="font-semibold text-black">{allPositions.length}</span> position{allPositions.length === 1 ? "" : "s"} ·{" "}
          <span className="font-semibold text-amber-600">{pendingCount}</span> pending ·{" "}
          <span className="font-semibold text-emerald-600">{approvedCount}</span> approved
        </div>
        <GuideLink section="nominations" label="How nominations work" className="ml-auto" />
      </div>

      {election.nomination_deadline && (
        <div data-testid="nom-deadline-banner" className={`inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-2 border font-medium ${new Date(election.nomination_deadline) < new Date() ? "bg-zinc-100 border-zinc-200 text-zinc-600" : "bg-[#1F6FE5] border-[#1F6FE5] text-white"}`}>
          <Clock size={15} weight="fill" />
          {new Date(election.nomination_deadline) < new Date()
            ? <>Nominations closed on {new Date(election.nomination_deadline).toLocaleString()} — the public link no longer accepts entries.</>
            : <>Nominations close {new Date(election.nomination_deadline).toLocaleString()}.</>}
        </div>
      )}

      {nominees.length === 0 && (
        <div className="text-center py-16 border border-dashed border-zinc-200 rounded-xl" data-testid="nom-empty">
          <UserCircle size={40} className="mx-auto text-zinc-300 mb-3" weight="duotone" />
          <p className="text-zinc-500 text-sm">No nominations yet. They'll appear here grouped by position, awaiting your approval.</p>
        </div>
      )}

      <div className="space-y-6">
        {grouped.map(({ position, items }) => (
          <div key={position} className="border border-zinc-200 rounded-xl overflow-hidden" data-testid={`nom-group-${position}`}>
            <div className="flex items-center justify-between bg-zinc-50 px-4 py-3 border-b border-zinc-200">
              <h3 className="font-display font-semibold tracking-tight text-base">{position}</h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-zinc-500">{items.length} nominee{items.length === 1 ? "" : "s"}</span>
                {items.filter((n) => n.status === "pending").length > 1 && (
                  <button data-testid={`nom-approve-all-${position}`} disabled={busy === `group-${position}`} onClick={() => approveAll(position, items)}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 active:scale-95">
                    {busy === `group-${position}` ? <SpinnerGap size={13} className="animate-spin" /> : <ThumbsUp size={13} weight="fill" />} Approve all ({items.filter((n) => n.status === "pending").length})
                  </button>
                )}
              </div>
            </div>
            {items.length === 0 ? (
              <p className="px-4 py-4 text-sm text-zinc-400">No nominees for this position yet.</p>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {items.map((n) => (
                  <li key={n.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`nom-row-${n.id}`}>
                    <div className="w-9 h-9 rounded-full bg-[#E8F0FD] flex items-center justify-center shrink-0 text-[#1F6FE5] font-bold text-sm">
                      {(n.name || "?").trim()[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <p className="font-medium text-sm">{n.name || "—"}</p>
                      <p className="text-xs text-zinc-500">
                        {[n.email, n.phone].filter(Boolean).join(" · ") || "no contact provided"}
                        {n.nominated_by && (n.nominated_by.name || n.nominated_by.email || n.nominated_by.phone)
                          ? ` · nominated by ${n.nominated_by.name || n.nominated_by.email || n.nominated_by.phone}`
                          : ""}
                      </p>
                    </div>
                    <StatusBadge status={n.status || "pending"} />
                    {n.status === "pending" && (
                      <div className="flex items-center gap-2">
                        <button data-testid={`nom-approve-${n.id}`} disabled={busy === n.id} onClick={() => approve(n)}
                          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold bg-[#1F6FE5] text-white hover:bg-[#195AB8] transition-colors disabled:opacity-50 active:scale-95">
                          {busy === n.id ? <SpinnerGap size={14} className="animate-spin" /> : <ThumbsUp size={14} weight="fill" />} Approve
                        </button>
                        <button data-testid={`nom-reject-${n.id}`} disabled={busy === n.id} onClick={() => { setRejectTarget(n); setRejectReason(""); }}
                          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium border border-zinc-300 text-zinc-700 hover:border-red-400 hover:text-red-600 transition-colors disabled:opacity-50 active:scale-95">
                          <Prohibit size={14} /> Reject
                        </button>
                      </div>
                    )}
                    {n.status === "approved" && <CheckCircle size={18} weight="fill" className="text-emerald-500" />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Reject nomination</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 -mt-2">Rejecting <span className="font-medium text-black">{rejectTarget?.name}</span>. Add an optional reason (shared with the nominee if contactable).</p>
          <Textarea data-testid="nom-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (optional)" className="mt-1" />
          <DialogFooter>
            <button onClick={() => setRejectTarget(null)} className="px-4 py-2 rounded-md text-sm font-medium border border-zinc-300">Cancel</button>
            <button data-testid="nom-reject-confirm" disabled={busy === rejectTarget?.id} onClick={doReject} className="px-4 py-2 rounded-md text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50">Reject nomination</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
